import React, { useState, useEffect, useRef, useCallback } from 'react';
import { FaCalendarPlus, FaTimes, FaClock, FaDoorOpen, FaSpinner } from 'react-icons/fa';
import API from '../../API';
import { useOverlay } from '../../contexts/FocusContext';

interface Room {
  room_id: string;
  room_name: string;
  event_id: string;
  chat_room_arn: string;
  stage_arn: string;
}

interface BookRoomProps {
  accessToken: string;
  eventId: string;
  rooms: Room[];
  onBookingCreated?: () => void;
  isStaff?: boolean; // Whether the current user is Intersect staff
}

// Main room ID that requires staff permissions to book
const MAIN_ROOM_ID = 'intersect-main';

// Helper to directly control Unity keyboard capture
const setUnityKeyboardCapture = (capture: boolean) => {
  if ((window as any).sendMessageToUnity) {
    try {
      const captureKeyboardData = {
        eventType: 'CaptureKeyboard',
        payload: JSON.stringify({ capture })
      };
      (window as any).sendMessageToUnity('BrowserBridge', 'ReceivePayload', JSON.stringify(captureKeyboardData));
    } catch (error) {
      console.error('[BookRoom] Error setting Unity capture:', error);
    }
  }
};

// Helper to blur the Unity canvas so it stops intercepting keyboard events
const blurUnityCanvas = () => {
  // Try to find and blur the Unity canvas
  const canvas = document.querySelector('#unity-canvas') as HTMLCanvasElement;
  if (canvas) {
    canvas.blur();
    // Temporarily make it non-focusable
    canvas.setAttribute('tabindex', '-1');
  }
  // Also try the container
  const container = document.querySelector('#unity-container') as HTMLElement;
  if (container) {
    container.blur();
  }
};

// Helper to restore Unity canvas focus capability
const restoreUnityCanvas = () => {
  const canvas = document.querySelector('#unity-canvas') as HTMLCanvasElement;
  if (canvas) {
    canvas.removeAttribute('tabindex');
  }
};

const BookRoom: React.FC<BookRoomProps> = ({ accessToken, eventId, rooms, onBookingCreated, isStaff = false }) => {
  // Filter rooms - non-staff users cannot book the main room
  // Memoize to prevent unnecessary re-renders
  const availableRooms = React.useMemo(() => {
    return isStaff 
      ? rooms 
      : rooms.filter(room => room.room_id !== MAIN_ROOM_ID);
  }, [rooms, isStaff]);
  const [isOpen, setIsOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  
  // Track if any input in the form is focused - this releases Unity keyboard capture
  const [isInputFocused, setIsInputFocused] = useState(false);
  
  // Ref for title input to focus on modal open
  const titleInputRef = useRef<HTMLInputElement>(null);

  // Register as overlay when modal is open AND an input is focused
  // This releases Unity capture so user can type in the form
  useOverlay('book-room-form', isOpen && isInputFocused);

  // Handle focus - release Unity keyboard capture and blur canvas
  const handleInputFocus = useCallback(() => {
    setIsInputFocused(true);
    setUnityKeyboardCapture(false);
    blurUnityCanvas();
  }, []);

  // Handle blur - re-enable Unity keyboard capture
  const handleInputBlur = useCallback(() => {
    setIsInputFocused(false);
    // Small delay before re-enabling to allow clicking other inputs
    setTimeout(() => {
      // Only re-enable if no input is focused AND modal is closed
      if (document.activeElement?.tagName !== 'INPUT' && 
          document.activeElement?.tagName !== 'TEXTAREA' &&
          document.activeElement?.tagName !== 'SELECT') {
        // Don't re-enable Unity capture while modal is still open
        // It will be re-enabled when modal closes
      }
    }, 100);
  }, []);

  // Intercept ALL keyboard events when modal is open and prevent them from reaching Unity
  useEffect(() => {
    if (!isOpen) return;

    const stopKeyboardForUnity = (e: KeyboardEvent) => {
      // Check if we're typing in an input/textarea/select
      const target = e.target as HTMLElement;
      const isFormElement = target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.tagName === 'SELECT';
      
      if (isFormElement) {
        // Stop the event from bubbling up to Unity's listeners
        e.stopPropagation();
      }
    };

    // Capture phase to intercept before Unity gets it
    window.addEventListener('keydown', stopKeyboardForUnity, true);
    window.addEventListener('keyup', stopKeyboardForUnity, true);
    window.addEventListener('keypress', stopKeyboardForUnity, true);

    return () => {
      window.removeEventListener('keydown', stopKeyboardForUnity, true);
      window.removeEventListener('keyup', stopKeyboardForUnity, true);
      window.removeEventListener('keypress', stopKeyboardForUnity, true);
    };
  }, [isOpen]);

  // Focus the title input when modal opens
  useEffect(() => {
    if (isOpen) {
      // Immediately blur the Unity canvas when modal opens
      blurUnityCanvas();
      setUnityKeyboardCapture(false);
      
      const focusTimer = setTimeout(() => {
        if (titleInputRef.current) {
          titleInputRef.current.focus();
        }
      }, 100);
      return () => clearTimeout(focusTimer);
    } else {
      // Re-enable Unity capture when modal closes
      restoreUnityCanvas();
      setUnityKeyboardCapture(true);
    }
  }, [isOpen]);

  // Form state
  const [selectedRoomId, setSelectedRoomId] = useState('');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [date, setDate] = useState('');
  const [time, setTime] = useState('');
  const [duration, setDuration] = useState(30);

  // Live UTC time clock
  const [currentUtc, setCurrentUtc] = useState(new Date());
  
  // Update UTC clock every second when modal is open
  useEffect(() => {
    if (!isOpen) return;
    
    const interval = setInterval(() => {
      setCurrentUtc(new Date());
    }, 1000);
    
    return () => clearInterval(interval);
  }, [isOpen]);

  // Track if modal was just opened (to avoid resetting on every render)
  const wasOpenRef = useRef(false);
  
  // Reset form ONLY when modal first opens (not on every render)
  useEffect(() => {
    // Only reset when transitioning from closed to open
    if (isOpen && !wasOpenRef.current) {
      setSelectedRoomId(availableRooms.length > 0 ? availableRooms[0].room_id : '');
      setTitle('');
      setDescription('');
      setError(null);
      setSuccess(false);
      
      // Set default date to today in UTC
      const now = new Date();
      setDate(now.toISOString().split('T')[0]);
      
      // Set default time to next hour in UTC
      const nextHourUtc = new Date(now.getTime() + 60 * 60 * 1000);
      const utcHours = nextHourUtc.getUTCHours();
      setTime(`${utcHours.toString().padStart(2, '0')}:00`);
    }
    wasOpenRef.current = isOpen;
  }, [isOpen, availableRooms]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsSubmitting(true);

    try {
      // Construct ISO8601 start time in UTC
      const startDateTime = new Date(`${date}T${time}:00Z`);
      
      const bookingData = {
        eventId: eventId, // Include eventId for URL construction
        room_id: selectedRoomId,
        title: title.trim(),
        description: description.trim(),
        start_time: startDateTime.toISOString(),
        duration_minutes: duration
      };

      const response = await API.createBooking(accessToken, bookingData);
      
      if (response.status === 201) {
        setSuccess(true);
        setTimeout(() => {
          setIsOpen(false);
          setSuccess(false);
          if (onBookingCreated) {
            onBookingCreated();
          }
        }, 1500);
      } else if (response.status === 409) {
        const errorData = await response.text();
        setError(`Time slot conflict: ${errorData}`);
      } else {
        const errorData = await response.text();
        setError(errorData || 'Failed to create booking');
      }
    } catch (err) {
      setError('Network error. Please try again.');
      console.error('Error creating booking:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const selectedRoom = rooms.find(r => r.room_id === selectedRoomId);

  return (
    <>
      {/* Book Room Button */}
      <button
        onClick={(e) => {
          e.stopPropagation();
          setIsOpen(true);
        }}
        className="px-4 py-2 bg-green-600 bg-opacity-80 text-white rounded-lg hover:bg-opacity-100 transition-all duration-200 font-semibold flex items-center gap-2"
        title="Book a Room"
      >
        <FaCalendarPlus />
        Book Room
      </button>

      {/* Booking Modal */}
      {isOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-[10001]"
          onClick={() => !isSubmitting && setIsOpen(false)}
        >
          <div 
            className="bg-[#1a1a1a] rounded-lg shadow-2xl border border-[#333] w-[90%] max-w-lg"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex justify-between items-center p-6 border-b border-[#333]">
              <h2 className="text-2xl font-bold text-white flex items-center gap-3">
                <FaCalendarPlus className="text-green-500" />
                Book a Room
              </h2>
              <button
                onClick={() => !isSubmitting && setIsOpen(false)}
                className="text-gray-400 hover:text-white transition-colors p-2 hover:bg-[#2a2a2a] rounded"
                disabled={isSubmitting}
              >
                <FaTimes size={20} />
              </button>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              {/* Success Message */}
              {success && (
                <div className="p-4 bg-green-900/30 border border-green-500/50 rounded-lg text-green-300 text-center">
                  Booking created successfully!
                </div>
              )}

              {/* Error Message */}
              {error && (
                <div className="p-4 bg-red-900/30 border border-red-500/50 rounded-lg text-red-300">
                  {error}
                </div>
              )}

              {/* Room Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  <FaDoorOpen className="inline mr-2" />
                  Select Room
                </label>
                <select
                  value={selectedRoomId}
                  onChange={(e) => setSelectedRoomId(e.target.value)}
                  onFocus={handleInputFocus}
                  onBlur={handleInputBlur}
                  className="w-full px-4 py-3 bg-[#2a2a2a] border border-[#444] rounded-lg text-white focus:border-green-500 focus:outline-none"
                  required
                  disabled={isSubmitting}
                >
                  {availableRooms.map((room) => (
                    <option key={room.room_id} value={room.room_id}>
                      {room.room_id === MAIN_ROOM_ID 
                        ? `❗ ${room.room_name} (Staff Only)` 
                        : room.room_name}
                    </option>
                  ))}
                </select>
                {/* Staff indicator for main room */}
                {isStaff && selectedRoomId === MAIN_ROOM_ID && (
                  <p className="text-xs text-yellow-400 mt-2 flex items-center gap-1">
                    ❗ You have staff privileges to book the main room
                  </p>
                )}
              </div>

              {/* Title */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Title
                </label>
                <input
                  ref={titleInputRef}
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  onFocus={handleInputFocus}
                  onBlur={handleInputBlur}
                  placeholder="Enter booking title"
                  className="w-full px-4 py-3 bg-[#2a2a2a] border border-[#444] rounded-lg text-white placeholder-gray-500 focus:border-green-500 focus:outline-none"
                  required
                  maxLength={100}
                  disabled={isSubmitting}
                />
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Description (optional)
                </label>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  onFocus={handleInputFocus}
                  onBlur={handleInputBlur}
                  placeholder="Enter booking description"
                  className="w-full px-4 py-3 bg-[#2a2a2a] border border-[#444] rounded-lg text-white placeholder-gray-500 focus:border-green-500 focus:outline-none resize-none"
                  rows={2}
                  maxLength={500}
                  disabled={isSubmitting}
                />
              </div>

              {/* Current UTC Time Display */}
              <div className="bg-[#1a1a1a] border border-[#333] rounded-lg p-3 text-center">
                <div className="text-xs text-gray-400 mb-1">Current UTC Time</div>
                <div className="text-lg font-mono text-green-400">
                  {(currentUtc.getUTCMonth() + 1).toString().padStart(2, '0')}/{currentUtc.getUTCDate().toString().padStart(2, '0')}/{currentUtc.getUTCFullYear()} {currentUtc.toISOString().slice(11, 19)}
                </div>
              </div>

              {/* Date and Time */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Date (UTC)
                  </label>
                  <input
                    type="date"
                    value={date}
                    onChange={(e) => setDate(e.target.value)}
                    onFocus={handleInputFocus}
                    onBlur={handleInputBlur}
                    className="w-full px-4 py-3 bg-[#2a2a2a] border border-[#444] rounded-lg text-white focus:border-green-500 focus:outline-none"
                    required
                    disabled={isSubmitting}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Time (UTC, 24h)
                  </label>
                  <input
                    type="time"
                    value={time}
                    onChange={(e) => setTime(e.target.value)}
                    onFocus={handleInputFocus}
                    onBlur={handleInputBlur}
                    step="60"
                    className="w-full px-4 py-3 bg-[#2a2a2a] border border-[#444] rounded-lg text-white focus:border-green-500 focus:outline-none [&::-webkit-calendar-picker-indicator]:filter [&::-webkit-calendar-picker-indicator]:invert"
                    required
                    disabled={isSubmitting}
                  />
                  <div className="text-xs text-gray-500 mt-1">e.g. 14:00 = 2:00 PM</div>
                </div>
              </div>

              {/* Duration */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  <FaClock className="inline mr-2" />
                  Duration
                </label>
                <select
                  value={duration}
                  onChange={(e) => setDuration(parseInt(e.target.value))}
                  onFocus={handleInputFocus}
                  onBlur={handleInputBlur}
                  className="w-full px-4 py-3 bg-[#2a2a2a] border border-[#444] rounded-lg text-white focus:border-green-500 focus:outline-none"
                  disabled={isSubmitting}
                >
                  <option value={15}>15 minutes</option>
                  <option value={30}>30 minutes</option>
                  <option value={45}>45 minutes</option>
                  <option value={60}>1 hour</option>
                  <option value={90}>1.5 hours</option>
                  <option value={120}>2 hours</option>
                  <option value={180}>3 hours</option>
                  <option value={240}>4 hours</option>
                </select>
              </div>

              {/* Submit Button */}
              <div className="pt-4">
                <button
                  type="submit"
                  disabled={isSubmitting || !title.trim() || !selectedRoomId}
                  className="w-full px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-all duration-200 font-semibold flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? (
                    <>
                      <FaSpinner className="animate-spin" />
                      Creating...
                    </>
                  ) : (
                    <>
                      <FaCalendarPlus />
                      Create Booking
                    </>
                  )}
                </button>
              </div>

              {/* Timezone Note */}
              <p className="text-xs text-gray-500 text-center">
                All times are in UTC (Coordinated Universal Time)
              </p>
            </form>
          </div>
        </div>
      )}
    </>
  );
};

export default BookRoom;
